import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { listOpportunities } from "../lib/api";
import { fmtMoney, fmtPct } from "../components/OpportunityIntelligence";
import { Button } from "../components/ui/button";

const DEC_CLS = {
  BUY: "bg-emerald-600 text-white", NEGOTIATE: "bg-amber-500 text-white",
  PASS: "bg-rose-600 text-white", INSUFFICIENT_DATA: "bg-slate-500 text-white",
};
const LVL = {
  Low: "text-emerald-700", Medium: "text-amber-700", High: "text-rose-700",
};

export default function IntelligenceHome() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState(null);

  useEffect(() => {
    if (user) listOpportunities().then((d) => setItems(d.opportunities || [])).catch(() => setItems([]));
  }, [user]);

  if (user === null) return <><div className="p-10 text-center text-slate-400">Checking access…</div></>;
  if (!user) {
    return <><div className="max-w-xl mx-auto py-24 text-center">
      <span className="text-[11px] uppercase tracking-widest text-slate-400 font-mono">Atlas · European Vehicle Acquisition Intelligence</span>
      <h1 className="text-3xl font-bold text-atlas-ink mt-2">Know what to buy, where, and what to pay</h1>
      <p className="text-slate-500 mt-3">See your expected margin before you buy. Sign in to access your acquisition opportunities.</p>
      <Button className="mt-5 bg-atlas-blue text-white" onClick={() => navigate("/login")} data-testid="intel-login-btn">Sign in</Button>
    </div></>;
  }

  const isAdmin = user.role === "admin";
  return (
    <>
      <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-8">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <span className="text-[11px] uppercase tracking-widest text-slate-400 font-mono">Atlas Intelligence</span>
            <h1 className="text-3xl font-bold text-atlas-ink mt-1">Acquisition opportunities</h1>
            <p className="text-slate-500 text-sm mt-1">Find better vehicles to buy. Know what to pay. See your expected margin across Europe.</p>
          </div>
          <div className="flex gap-2">
            <Link to="/dealer-profile"><Button variant="outline" className="h-10" data-testid="intel-profile-btn">Dealer profile</Button></Link>
            {isAdmin && <Link to="/intelligence/new"><Button className="h-10 bg-atlas-blue hover:bg-atlas-sky text-white" data-testid="intel-new-btn">+ New analysis</Button></Link>}
          </div>
        </div>

        {items === null ? (
          <div className="text-slate-400 py-16 text-center">Loading opportunities…</div>
        ) : items.length === 0 ? (
          <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-12 text-center" data-testid="intel-empty">
            <p className="text-slate-500">No opportunities yet.</p>
            {isAdmin && <Link to="/intelligence/new"><Button className="mt-4 bg-atlas-blue text-white">Create the first analysis</Button></Link>}
          </div>
        ) : (
          <div className="grid gap-3" data-testid="intel-list">
            {items.map((o) => {
              const v = o.vehicle || {}, a = o.acquisition || {};
              return (
                <Link key={o.id} to={`/intelligence/${o.id}`} data-testid={`intel-row-${o.id}`}
                  className="rounded-lg border border-slate-200 bg-white p-4 hover:border-atlas-blue transition-colors flex flex-wrap items-center gap-4 justify-between">
                  <div className="min-w-[220px]">
                    <div className="font-semibold text-atlas-ink">{[v.make, v.model, v.year].filter(Boolean).join(" ")}</div>
                    <div className="text-xs text-slate-500">{a.sourceCountry} → {a.destinationCountry} · asking {fmtMoney(a.askingPrice, a.askingCurrency)}</div>
                  </div>
                  <div className="flex items-center gap-6 text-sm">
                    <div className="text-center"><div className="text-[10px] uppercase text-slate-400">Margin</div><div className="font-mono text-slate-800">{fmtMoney(o.grossMargin, o.destCurrency)} · {fmtPct(o.marginPct)}</div></div>
                    <div className="text-center"><div className="text-[10px] uppercase text-slate-400">Score</div><div className="font-mono text-slate-800">{o.opportunityScore ?? "—"}</div></div>
                    <div className="text-center"><div className="text-[10px] uppercase text-slate-400">Risk</div><div className={`font-medium ${LVL[o.riskLevel] || "text-slate-500"}`}>{o.riskLevel || "—"}</div></div>
                    <span className={`text-xs font-bold px-2.5 py-1 rounded-md ${DEC_CLS[o.decision] || DEC_CLS.INSUFFICIENT_DATA}`}>{(o.decision || "").replace("_", " ")}</span>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
